import React, { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import SEO from '../components/SEO'
import { useAuth } from '../contexts/AuthContext'
import { FiEye, FiEyeOff, FiLoader } from 'react-icons/fi'
import SafeIcon from '../common/SafeIcon'
import { trackSignUp } from '../services/analytics'
import { hapticSuccess, hapticError } from '../services/hapticsService'
import AppleSignInButton from '../components/auth/AppleSignInButton'
import { isApplePlatform } from '../services/platformService'
import { stashClaimTokenFromUrl } from '../utils/guestClaim'

// Password strength calculation
const getPasswordStrength = (password) => {
  if (!password) return 0
  let strength = 0
  if (password.length >= 6) strength++
  if (password.length >= 8) strength++
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength++
  if (/\d/.test(password)) strength++
  if (/[^a-zA-Z0-9]/.test(password)) strength++
  return Math.min(strength, 4)
}

const getStrengthColor = (strength) => {
  const colors = ['bg-subtle', 'bg-status-error', 'bg-orange-500', 'bg-yellow-500', 'bg-accent']
  return colors[strength] || colors[0]
}

const getStrengthLabel = (strength) => {
  const labels = ['', 'Weak', 'Fair', 'Good', 'Strong']
  return labels[strength] || ''
}

function SignUp() {
  const navigate = useNavigate()
  const { signUp } = useAuth()

  // Guest-link signup: stash ?claim={guest_token} so it survives the
  // email-confirmation redirect. Redeemed in AuthContext once signed in.
  useEffect(() => {
    stashClaimTokenFromUrl();
  }, []);

  // Add auth-page class for iOS safe area styling
  useEffect(() => {
    document.documentElement.classList.add('auth-page');
    document.body.classList.add('auth-page');
    return () => {
      document.documentElement.classList.remove('auth-page');
      document.body.classList.remove('auth-page');
    };
  }, []);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    acceptTerms: false
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
    if (error) setError('')
  }

  const fail = (msg) => {
    hapticError()
    setError(msg)
    return false
  }

  const validateForm = () => {
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.password || !formData.confirmPassword) {
      return fail('All fields are required')
    }

    if (formData.password.length < 6) {
      return fail('Password must be at least 6 characters')
    }

    if (formData.password !== formData.confirmPassword) {
      return fail('Passwords do not match')
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(formData.email)) {
      return fail('Enter a valid email address')
    }

    if (!formData.acceptTerms) {
      return fail('You must accept the Privacy Policy to continue')
    }

    return true
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateForm()) return

    setLoading(true)
    setError('')

    const userData = {
      first_name: formData.firstName,
      last_name: formData.lastName,
      full_name: `${formData.firstName} ${formData.lastName}`,
      tempPassword: formData.password
    }

    try {
      const { data, error } = await signUp(
        formData.email,
        formData.password,
        userData
      )

      if (data?.user?.id && !error) {
        trackSignUp(data.user.id)
        hapticSuccess()
      }

      navigate('/email-confirmation', {
        state: {
          email: formData.email,
          userData: userData,
          signupAttempt: true,
          hasExistingAccount: error?.message?.includes('already') || error?.message?.includes('registered') || false
        }
      })

    } catch (err) {
      navigate('/email-confirmation', {
        state: {
          email: formData.email,
          userData: userData,
          signupAttempt: true,
          hasError: true,
          errorMessage: err.message
        }
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-app flex items-center justify-center p-4 safe-area-inset-top">
      <SEO
        title="Create Account | TeeSync"
        description="Join TeeSync for free. Create golf groups, schedule rounds, check weather forecasts, and coordinate tee times with friends."
        canonical="/signup"
      />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="max-w-md w-full"
      >
        {/* Heading - left-aligned, no logo block, to match the mockup */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-text-primary mb-2">
            Create your account
          </h1>
          <p className="text-sm text-text-secondary">
            A few details and you're in. We'll never share your email.
          </p>
        </div>

        {/* SignUp Form */}
        <div className="bg-surface rounded-lg border border-border-subtle shadow-card p-6">
          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="bg-status-error/10 border border-status-error/30 text-status-error px-4 py-3 rounded-md text-sm">
                {error}
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="firstName" className="block text-sm font-medium text-text-secondary mb-2">
                  First Name
                </label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  autoComplete="given-name"
                  value={formData.firstName}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-border-subtle rounded-md focus:ring-1 focus:ring-accent focus:border-accent bg-surface text-text-primary placeholder-text-muted"
                  placeholder="John"
                  required
                />
              </div>

              <div>
                <label htmlFor="lastName" className="block text-sm font-medium text-text-secondary mb-2">
                  Last Name
                </label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  autoComplete="family-name"
                  value={formData.lastName}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-border-subtle rounded-md focus:ring-1 focus:ring-accent focus:border-accent bg-surface text-text-primary placeholder-text-muted"
                  placeholder="Doe"
                  required
                />
              </div>
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-text-secondary mb-2">
                Email
              </label>
              <input
                type="email"
                id="email"
                name="email"
                autoComplete="email"
                inputMode="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-border-subtle rounded-md focus:ring-1 focus:ring-accent focus:border-accent bg-surface text-text-primary placeholder-text-muted"
                placeholder="you@example.com"
                required
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-text-secondary mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  autoComplete="new-password"
                  value={formData.password}
                  onChange={handleChange}
                  className="w-full px-4 py-3 pr-12 border border-border-subtle rounded-md focus:ring-1 focus:ring-accent focus:border-accent bg-surface text-text-primary placeholder-text-muted text-base font-sans"
                  placeholder="Min 6 characters"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-text-muted hover:text-text-secondary"
                >
                  <SafeIcon icon={showPassword ? FiEyeOff : FiEye} className="w-5 h-5" />
                </button>
              </div>
              {formData.password && (
                <div className="mt-2">
                  <div className="flex gap-1">
                    {[1, 2, 3, 4].map((level) => (
                      <div
                        key={level}
                        className={`h-1 flex-1 rounded-full transition-colors ${
                          level <= getPasswordStrength(formData.password)
                            ? getStrengthColor(getPasswordStrength(formData.password))
                            : 'bg-subtle'
                        }`}
                      />
                    ))}
                  </div>
                  <p className="text-xs text-text-muted mt-1">
                    {getStrengthLabel(getPasswordStrength(formData.password))}
                  </p>
                </div>
              )}
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-text-secondary mb-2">
                Confirm Password
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className="w-full px-4 py-3 pr-12 border border-border-subtle rounded-md focus:ring-1 focus:ring-accent focus:border-accent bg-surface text-text-primary placeholder-text-muted"
                  placeholder="Re-enter password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-text-muted hover:text-text-secondary"
                >
                  <SafeIcon icon={showConfirmPassword ? FiEyeOff : FiEye} className="w-5 h-5" />
                </button>
              </div>
            </div>

            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                name="acceptTerms"
                checked={formData.acceptTerms}
                onChange={(e) => setFormData({ ...formData, acceptTerms: e.target.checked })}
                className="mt-0.5 w-4 h-4 text-accent border-border-subtle rounded focus:ring-accent"
              />
              <span className="text-sm text-text-secondary">
                I agree to the{' '}
                <Link to="/privacy" className="text-accent hover:underline" target="_blank">
                  Privacy Policy
                </Link>
              </span>
            </label>

            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center px-4 py-3 bg-accent text-white rounded-md hover:bg-interactive-hover disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
            >
              {loading ? (
                <SafeIcon icon={FiLoader} className="w-5 h-5 animate-spin" />
              ) : (
                'Continue →'
              )}
            </button>
          </form>

          {isApplePlatform() && (
            <>
              <div className="my-6 flex items-center gap-3">
                <div className="flex-1 h-px bg-border-subtle" />
                <span className="text-xs uppercase tracking-wide text-text-muted">or</span>
                <div className="flex-1 h-px bg-border-subtle" />
              </div>

              <AppleSignInButton
                mode="signup"
                onError={(msg) => setError(msg)}
              />
            </>
          )}

          <div className="mt-6 text-center text-sm text-text-secondary">
            Already have an account?{' '}
            <Link to="/login" className="text-accent hover:underline font-medium">
              Sign in
            </Link>
          </div>
        </div>
      </motion.div>
    </div>
  )
}

export default SignUp
